import { useMemo, useState } from 'react';
import { ArrowRight, TableProperties } from 'lucide-react';
import { Button } from '../ui';
import {
  contentIngestionService,
  type FieldMapping,
  type IngestionRun,
  type MappableEntityType,
  type SourceFile,
  type SourceTable,
} from '../../services/contentIngestionService';

const FIELDS: Record<MappableEntityType, Array<{ key: string; label: string; required?: boolean }>> = {
  session: [
    { key: 'external_id', label: 'External ID' }, { key: 'title', label: 'Title', required: true },
    { key: 'description', label: 'Description' }, { key: 'type', label: 'Type' }, { key: 'track', label: 'Track' },
    { key: 'date', label: 'Date', required: true }, { key: 'start_time', label: 'Start time', required: true },
    { key: 'end_time', label: 'End time', required: true }, { key: 'room', label: 'Room' }, { key: 'status', label: 'Status' },
  ],
  speaker: [
    { key: 'external_id', label: 'External ID' }, { key: 'display_name', label: 'Display name', required: true },
    { key: 'email', label: 'Email' }, { key: 'title', label: 'Title' }, { key: 'affiliation', label: 'Affiliation' },
    { key: 'bio', label: 'Bio' }, { key: 'linkedin', label: 'LinkedIn' }, { key: 'portfolio', label: 'Portfolio' },
    { key: 'avatar_url', label: 'Avatar URL' }, { key: 'interests', label: 'Interests' },
    { key: 'is_featured', label: 'Featured' }, { key: 'featured_order', label: 'Featured order' },
  ],
};

interface TableState {
  key: string;
  file: SourceFile;
  table: SourceTable;
  entityType: MappableEntityType;
  columns: Record<string, string>;
}

function initialTables(run: IngestionRun): TableState[] {
  return run.files.flatMap((file) => file.tables.map((table) => {
    const candidates = file.mappings.filter((mapping) => mapping.table_key === table.table_key);
    const mapping = candidates.sort((left, right) => right.revision - left.revision)[0];
    return {
      key: `${file.id}:${table.table_key}`,
      file,
      table,
      entityType: mapping?.entity_type ?? 'session',
      columns: { ...(mapping?.mapping.columns ?? {}) },
    };
  }));
}

function complete(item: TableState) {
  return FIELDS[item.entityType].filter((field) => field.required).every((field) => Boolean(item.columns[field.key]));
}

export default function MappingStep({ eventId, run, onReadyForReview }: {
  eventId: string;
  run: IngestionRun;
  onReadyForReview: (run: IngestionRun) => void;
}) {
  const [tables, setTables] = useState(() => initialTables(run));
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canProcess = useMemo(() => tables.length > 0 && tables.every(complete), [tables]);

  function update(key: string, change: Partial<Pick<TableState, 'entityType' | 'columns'>>) {
    setTables((current) => current.map((item) => item.key === key ? { ...item, ...change } : item));
  }

  async function process() {
    if (!canProcess) return;
    setProcessing(true);
    setError(null);
    try {
      for (const item of tables) {
        const mapping: FieldMapping = { entity_type: item.entityType, columns: item.columns };
        await contentIngestionService.confirmMapping(eventId, run.id, item.file.id, { table_key: item.table.table_key, mapping });
      }
      onReadyForReview(await contentIngestionService.process(eventId, run.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Content processing failed');
    } finally {
      setProcessing(false);
    }
  }

  return <section className="space-y-4" aria-label="Column mapping">
    <div>
      <h2 className="text-lg font-semibold text-white">Map uploaded columns</h2>
      <p className="mt-1 text-sm text-gray-400">Confirm what each table contains and map the required fields.</p>
    </div>
    {tables.map((item) => <section key={item.key} aria-label={`${item.table.sheet_name ?? item.table.filename} mapping`} className="rounded-2xl border border-gray-800 bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-4">
        <div><h3 className="font-medium text-white">{item.table.sheet_name ?? item.table.filename}</h3><p className="text-xs text-gray-500">{item.table.row_count} rows · {item.table.headers.join(', ')}</p></div>
        <TableProperties size={19} className="text-sky-400" />
      </div>
      <label className="mt-4 block text-xs font-medium text-gray-300">Content type
        <select value={item.entityType} onChange={(event) => update(item.key, { entityType: event.target.value as MappableEntityType, columns: {} })} className="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm">
          <option value="session">Sessions</option><option value="speaker">Speakers</option>
        </select>
      </label>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {FIELDS[item.entityType].map((field) => <label key={field.key} className="text-xs font-medium text-gray-300">{field.label}{field.required ? ' *' : ''}
          <select aria-label={field.label} value={item.columns[field.key] ?? ''} onChange={(event) => update(item.key, { columns: { ...item.columns, [field.key]: event.target.value } })} className="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm">
            <option value="">Not mapped</option>{item.table.headers.map((header) => <option key={header} value={header}>{header}</option>)}
          </select>
        </label>)}
      </div>
      {item.table.sample_rows.length > 0 && <div className="mt-4 overflow-x-auto"><table className="min-w-full text-left text-xs"><thead><tr>{item.table.headers.map((header) => <th key={header} className="border-b border-gray-800 px-2 py-1 text-gray-500">{header}</th>)}</tr></thead><tbody>{item.table.sample_rows.slice(0, 3).map((row, index) => <tr key={index}>{item.table.headers.map((header) => <td key={header} className="border-b border-gray-800/60 px-2 py-1 text-gray-300">{String(row[header] ?? '')}</td>)}</tr>)}</tbody></table></div>}
    </section>)}
    {error && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300">{error}</p>}
    <div className="flex justify-end"><Button icon={<ArrowRight size={14} />} disabled={!canProcess || processing} loading={processing} onClick={() => void process()}>Process content</Button></div>
  </section>;
}
